import React, { useState } from 'react';
import { Card, OverlayTrigger, Popover, Modal, Row, Col } from 'react-bootstrap';
import { formatDate } from '../../../../_metronic/utils/utils';

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);

const XIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);

const TimelineIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6C63FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
);

/**
 * Derives the 4 timeline steps for a given renewal.
 * Step 3 and 4 branch based on actual outcome:
 *   step 3: "Proposal Accepted" or "Proposal Rejected"
 *   step 4: "Renewed" or "Lost"
 */
const getTimelineSteps = (renewal) => {
    const proposalStatus = renewal.latestPmsProposal?.status;
    const state = renewal.state;

    // Force proposal rejected if renewal is discontinued
    const isLost = state === "DISCONTINUED";

    const step3Label = isLost
        ? "Proposal Rejected"
        : proposalStatus === "REJECTED"
            ? "Proposal Rejected"
            : "Proposal Accepted";

    const step4Label = isLost ? "Lost" : "Renewed";

    const step3IsNegative = isLost || proposalStatus === "REJECTED";
    const step4IsNegative = isLost;

    return [
        { key: "START_INTIMATION", label: "Start Intimation", negative: false },
        { key: "PROPOSAL_SENT", label: "Proposal Sent", negative: false },
        {
            key: "PROPOSAL_OUTCOME",
            label: step3Label,
            negative: step3IsNegative,
        },
        {
            key: "FINAL_OUTCOME",
            label: step4Label,
            negative: step4IsNegative,
        },
    ];
};

/**
 * Determines how far along the renewal is (0-based index into the 4 steps).
 * Because this reads the LATEST proposal's status, a rejection followed by
 * a newly sent proposal naturally resets progress back to "Proposal Sent" (index 1) —
 * no separate "retry" flag needed, matching the desired behavior.
 */
const getCurrentStepIndex = (renewal) => {
    if (renewal.state === 'RENEWED' || renewal.state === 'DISCONTINUED') return 3;
    if (renewal.latestPmsProposal?.status === 'ACCEPTED' || renewal.latestPmsProposal?.status === 'REJECTED') return 2;
    if (renewal.latestPmsProposal?.status === 'SENT') return 1;
    if (renewal.isNotify === true) return 0;
    return -1; // nothing started yet
};

/**
 * Renders the full field list for a PMS proposal — shared by the hover
 * popover and the click modal so there's a single source of truth.
 */
const ProposalDetails = ({ proposal }) => (
    <Row>
        <Col xs={6} className="mb-2"><strong>Status</strong><div>{proposal.status || 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Owner Name</strong><div>{proposal.owner?.fullName || 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Owner Email</strong><div>{proposal.owner?.email || 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Current Rent</strong><div>{proposal.currentRent || 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Revised Rent</strong><div>{proposal.revisedRent || 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Maintenance Included</strong><div>{proposal.maintenanceIncluded ? 'Yes' : 'No'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Start Date</strong><div>{proposal.newSubscriptionStartDate ? formatDate(proposal.newSubscriptionStartDate) : 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>End Date</strong><div>{proposal.newSubscriptionEndDate ? formatDate(proposal.newSubscriptionEndDate) : 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Fee Rate</strong><div>{proposal.feeRateQuantity ?? 'N/A'} {proposal.feeRateUnit || ''}</div></Col>
        <Col xs={6} className="mb-2"><strong>Payment Interval</strong><div>{proposal.feePaymentInterval || 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Payment Mechanism</strong><div>{proposal.feePaymentMechanism || 'N/A'}</div></Col>
        <Col xs={6} className="mb-2"><strong>Payment Charge Mode</strong><div>{proposal.paymentChargeMode || 'N/A'}</div></Col>
        {proposal.description && <Col xs={12} className="mb-2"><strong>Remarks</strong><div>{proposal.description}</div></Col>}
        {proposal.rejectionReason && <Col xs={12} className="mb-2"><strong>Rejection Reason</strong><div>{proposal.rejectionReason}</div></Col>}
    </Row>
);

/**
 * RenewalTimeline
 * @param {object} renewal - the renewal record; steps + progress are derived from it
 */
const RenewalTimeline = ({ renewal }) => {
    const [showProposalModal, setShowProposalModal] = useState(false);
    const steps = getTimelineSteps(renewal || {});
    const currentIndex = getCurrentStepIndex(renewal || {});
    const proposal = renewal?.latestPmsProposal;

    return (
        <Card className="mb-3 border-0" style={{ background: '#FAFAFC' }}>
            <Card.Body className="py-3 px-4">
                <div className="d-flex align-items-center mb-4">
                    <span className="me-2 d-flex align-items-center">
                        <TimelineIcon />
                    </span>
                    <span className="fw-semibold px-2" style={{ fontSize: '0.95rem' }}>
                        Renewal Timeline
                    </span>
                </div>

                <div className="d-flex align-items-start position-relative" style={{ padding: '0 8px' }}>
                    {steps.map((step, index) => {
                        const isCompleted = index < currentIndex;
                        const isActive = index === currentIndex;
                        const isFuture = index > currentIndex;
                        const isLast = index === steps.length - 1;
                        const isNegative = step.negative && (isCompleted || isActive);

                        let circleBg = '#E9ECEF';
                        if (isCompleted || isActive) {
                            circleBg = isNegative ? '#DC3545' : (isCompleted ? '#28A745' : '#6C63FF');
                        }
                        const circleColor = isFuture ? '#ADB5BD' : '#FFFFFF';
                        const lineColor = index < currentIndex ? (steps[index].negative ? '#DC3545' : '#28A745') : '#E9ECEF';
                        const labelColor = isFuture ? '#ADB5BD' : isNegative ? '#DC3545' : isActive ? '#6C63FF' : '#495057';

                        let icon = index + 1;
                        if (isCompleted) icon = isNegative ? <XIcon /> : <CheckIcon />;
                        else if (isActive) icon = isNegative ? <XIcon /> : index + 1;

                        const isProposalStep = (step.key === 'PROPOSAL_SENT' || step.key === 'PROPOSAL_OUTCOME') && !!proposal;

                        const stepNode = (
                            <div key={step.key} className="d-flex flex-column align-items-center" style={{ flex: 1, position: 'relative' }}>

                                {!isLast && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 11,
                                            left: '50%',
                                            width: '100%',
                                            height: 2,
                                            background: lineColor,
                                            zIndex: 0,
                                        }}
                                    />
                                )}

                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle"
                                    style={{
                                        width: 24,
                                        height: 24,
                                        background: circleBg,
                                        color: circleColor,
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        zIndex: 1,
                                        flexShrink: 0,
                                        cursor: isProposalStep ? 'pointer' : 'default',
                                    }}
                                    onClick={() => isProposalStep && setShowProposalModal(true)}
                                >
                                    {icon}
                                </div>

                                <div
                                    className="text-center mt-2"
                                    style={{
                                        fontSize: '0.75rem',
                                        fontWeight: isActive ? 600 : 500,
                                        color: labelColor,
                                        maxWidth: 100,
                                        cursor: isProposalStep ? 'pointer' : 'default',
                                    }}
                                    onClick={() => isProposalStep && setShowProposalModal(true)}
                                >
                                    {step.label}
                                </div>
                            </div>
                        );

                        if (!isProposalStep) return stepNode;

                        return (
                            <OverlayTrigger
                                key={step.key}
                                placement="top"
                                overlay={
                                    <Popover id="proposal-popover" style={{ maxWidth: 320 }}>
                                        <Popover.Title as="h6">Proposal Details</Popover.Title>
                                        <Popover.Content>
                                            <ProposalDetails proposal={proposal} />
                                        </Popover.Content>
                                    </Popover>
                                }
                            >
                                {stepNode}
                            </OverlayTrigger>
                        );
                    })}
                </div>
            </Card.Body>

            <Modal show={showProposalModal} onHide={() => setShowProposalModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Proposal Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {proposal && <ProposalDetails proposal={proposal} />}
                </Modal.Body>
            </Modal>
        </Card>
    );
};

export default RenewalTimeline;
